/**
 * pages.js
 *
 * Builds the route/menu tree by combining:
 * 1. Menu structure (labels, icons, paths, hierarchy, order).
 * 2. Component registry (componentRegistry.js) — maps paths to React components.
 *
 * The sidebar is driven dynamically by the modules returned from
 * GET /admin/modules/permissions/{personId}. The backend returns the tree
 * already filtered to the user's allowed modules. `normalizeModules` maps
 * backend field names (module_id, name) to the format the sidebar expects
 * (id, label), and `buildPages` attaches the React components from the registry.
 */

import componentRegistry from './componentRegistry';

/**
 * Maps the backend module tree (from getModulePermissions) to the format
 * the Sidebar and Routes expect.
 *
 * Backend shape per node:
 *   { module_id, parent_id, name, icon, path, order/sort_order, is_active, children }
 *
 * Sidebar shape per node:
 *   { id, label, icon, path, order, children }
 *
 * @param {Array} backendModules - The `modules` array from the permissions endpoint.
 * @returns {Array} Normalized tree ready for `buildPages`.
 */
export function normalizeModules(backendModules = []) {
    if (!Array.isArray(backendModules) || backendModules.length === 0) {
        return [];
    }

    return backendModules
        // Nunca mostramos módulos dados de baja (soft-delete del CRUD).
        .filter((mod) => mod.is_active !== false)
        .map((mod) => {
            const node = {
                id: String(mod.module_id),
                label: mod.name,
                icon: mod.icon || 'ri-folder-line',
                path: mod.path || undefined,
                order: mod.order ?? mod.sort_order ?? 0,
            };

            const hasChildren = Array.isArray(mod.children) && mod.children.length > 0;
            if (hasChildren) {
                // Un contenedor filtra recursivamente sus hijos. El filtro por
                // `enabled` se aplica en las HOJAS, no en el contenedor: un padre
                // debe seguir visible mientras tenga al menos un hijo visible,
                // aunque su propio `enabled` sea false/indeterminado.
                node.children = normalizeModules(mod.children);
            }

            return { node, mod, hasChildren };
        })
        // Reglas de visibilidad:
        //  - Hoja (sin hijos): visible solo si enabled !== false.
        //  - Contenedor (con hijos): visible solo si le quedó al menos un hijo
        //    visible tras el filtrado recursivo.
        .filter(({ node, mod, hasChildren }) => {
            if (hasChildren) {
                return node.children.length > 0;
            }
            return mod.enabled !== false;
        })
        .map(({ node }) => node)
        .sort((a, b) => a.order - b.order);
}

/**
 * Recursively attaches `component` from the registry to menu items that have a `path`.
 * Items with a path that has NO matching component are EXCLUDED from the menu
 * to prevent broken navigation (blank pages).
 * Items without a path (group containers) are always kept unless all their
 * children were excluded.
 */
function attachComponents(menuItems) {
    return menuItems
        .map(item => {
            const result = { ...item };

            // Process children recursively first
            if (item.children && item.children.length > 0) {
                result.children = attachComponents(item.children);
            }

            // If it has a path AND has no children, it must have a component
            if (item.path && (!result.children || result.children.length === 0)) {
                if (componentRegistry[item.path]) {
                    result.component = componentRegistry[item.path];
                } else {
                    return null; // Leaf with no component → don't show
                }
            }

            // If it has a path AND has valid children, attach component if available
            // but don't exclude it (it's a group that also has a redirect path)
            if (item.path && result.children && result.children.length > 0) {
                if (componentRegistry[item.path]) {
                    result.component = componentRegistry[item.path];
                }
                // Keep it regardless — it has children to show
            }

            // If it's a group with no remaining children after filtering, exclude it
            if (!item.path && result.children && result.children.length === 0) {
                return null;
            }

            return result;
        })
        .filter(Boolean);
}

/**
 * Builds the full pages array from a menu structure.
 * @param {Array} menuStructure - Normalized menu tree (from the backend modules).
 * @returns {Array} Pages array compatible with the Sidebar component.
 */
export function buildPages(menuStructure) {
    return attachComponents(menuStructure);
}
